import axios, { InternalAxiosRequestConfig } from 'axios';
import {
  getLocalStorageValue,
  LocalStorageKey,
  setLocalStorageValue,
} from '../../utils/localStorage/localStorage';
import { TokenModel } from '../../domain/Auth';
import { jwtDecode } from 'jwt-decode';
import { apiRoutes } from '../../../config/api/apiRoutes';
import { LANGUAGE_LOCKED, LOCKED_LANGUAGE } from '../../../config/language';

export type SuccessResponse<T> = {
  data: T;
};

export type ErrorResponse = {
  response: {
    data: {
      message: string;
      statusCode: number;
    };
  };
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

instance.interceptors.request.use(async (config) => {
  await appendAuthorizationToken(config);
  await appendSelectedCompany(config);
  await appendSelectedLanguage(config);

  return config;
});

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const isTokenExpiringSoon = (token: string): boolean => {
  const decodedToken = jwtDecode(token);
  const currentTime = Date.now() / 1000;

  if (!decodedToken || !decodedToken.exp) {
    return true;
  }

  return decodedToken.exp - currentTime < 3600;
};

const performRefresh = async (
  tokenModel: TokenModel,
): Promise<TokenModel | null> => {
  if (!tokenModel.refreshToken) {
    return null;
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${apiRoutes.auth.refreshToken}`,
      { refreshToken: tokenModel.refreshToken },
    );

    setLocalStorageValue(LocalStorageKey.token, response.data);
    return response.data;
  } catch (error) {
    return null;
  }
};

// Single-flight refresh: concurrent callers (multiple requests near expiry, or a
// burst of 401s) share ONE rotation. Without this, each would rotate the same
// refresh token and the server's reuse-detection would revoke the whole session.
let inFlightRefresh: Promise<TokenModel | null> | null = null;

const refreshToken = (tokenModel: TokenModel): Promise<TokenModel | null> => {
  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh(tokenModel).finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
};

// Reads the stored token and attempts a refresh. Exposed so the response
// interceptor / AuthProvider can recover from a 401 before logging the user out.
export const tryRefreshStoredToken = (): Promise<TokenModel | null> => {
  const tokenModel = getLocalStorageValue<TokenModel>(LocalStorageKey.token);

  if (!tokenModel) {
    return Promise.resolve(null);
  }

  return refreshToken(tokenModel);
};

const appendAuthorizationToken = async (config: InternalAxiosRequestConfig) => {
  const tokenModel = getLocalStorageValue<TokenModel>(LocalStorageKey.token);

  if (!tokenModel) {
    return;
  }

  if (!config.headers) {
    return;
  }

  let activeToken = tokenModel.token;

  if (isTokenExpiringSoon(tokenModel.token)) {
    const refreshed = await refreshToken(tokenModel);

    if (refreshed?.token) {
      activeToken = refreshed.token;
    }
  }

  config.headers['Authorization'] = `Bearer ${activeToken}`;
};

const appendSelectedCompany = async (config: InternalAxiosRequestConfig) => {
  const selectedCompany = getLocalStorageValue<TokenModel>(
    LocalStorageKey.company,
  );

  if (!selectedCompany) {
    return;
  }

  if (!config.headers) {
    return;
  }

  config.headers['Company'] = selectedCompany;
};

const appendSelectedLanguage = async (config: InternalAxiosRequestConfig) => {
  // The stored value is ignored rather than defaulted over: users who picked a
  // language before the lock still have it in local storage.
  const selectedLanguage = LANGUAGE_LOCKED
    ? LOCKED_LANGUAGE
    : process.env.REACT_APP_LANGUAGE ??
      getLocalStorageValue<string>(LocalStorageKey.language);

  if (!selectedLanguage) {
    return;
  }

  if (!config.headers) {
    return;
  }

  config.headers['Language'] = selectedLanguage;
};

export default instance;
